import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

/**
 * Manual oversight of the scan's AI-written tasks (29 Oct meeting, "take action" button).
 * Shows the task the scan wrote for this release or campaign, or a plain starting point; the
 * admin edits it, then creates the task or dismisses the item. Nothing is applied to the site:
 * a task is ticked off by hand in the Action tasks list.
 *
 * `target` is { kind: "releases" | "campaigns", id, competitor, summary } or null when closed.
 */
export default function TakeActionDialog({ target, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("medium");
  const [fromScan, setFromScan] = useState(false);

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/admin/competitive-intel/${target.kind}/${target.id}/action`)
      .then((res) => {
        if (cancelled) return;
        const draft = res.data?.data || {};
        setText(draft.task_description || "");
        setPriority(draft.priority || "medium");
        setFromScan(Boolean(draft.from_scan));
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || "Could not load the suggested action.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const submit = async (mode) => {
    setSaving(true);
    setError("");
    try {
      await api.post(`/admin/competitive-intel/${target.kind}/${target.id}/take-action`, {
        mode,
        task_description: mode === "create" ? text.trim() : undefined,
        priority: mode === "create" ? priority : undefined,
      });
      toast.success(mode === "create" ? "Task added to Action tasks." : "Dismissed.");
      await onDone?.(target, mode);
      onClose();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError((errors && Object.values(errors).flat()[0]) || err.response?.data?.message || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canCreate = !loading && !saving && text.trim().length >= 3;

  return (
    <Dialog open={Boolean(target)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="take-action-title">
      <DialogTitle id="take-action-title" sx={{ pb: 0.5 }}>
        Take action{target?.competitor ? `: ${target.competitor}` : ""}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {target?.summary ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {target.summary}
          </Typography>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TextField
              label="Suggested action"
              helperText={fromScan ? "Written by the scan. Check it and edit before saving." : "A starting point. Edit it before saving."}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              inputProps={{ maxLength: 600 }}
            />
            <TextField select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ maxWidth: 200 }}>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </TextField>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f9fafb", color: "text.secondary", fontSize: 13 }}>
              Nothing changes on the site. &quot;Create task&quot; adds this to the Action tasks list, where it is ticked off by hand.
              &quot;Dismiss&quot; closes any open task for this item.
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="outlined" color="inherit" onClick={() => submit("dismiss")} disabled={loading || saving}>
          Dismiss
        </Button>
        <Button variant="contained" onClick={() => submit("create")} disabled={!canCreate}>
          {saving ? "Saving…" : "Create task"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
